// @flow
import Box from "@mui/material/Box";
import FormLabel from "@mui/material/FormLabel";
import TextField from "@mui/material/TextField";
import React, { useEffect, useRef, useState } from "react";
import Confirmation from "../confirmation/confirmation";
import DangerouslySetHtml from "../dangerouslySetHtml/dangerouslySetHtml";

type Props = {
	open: boolean,
};

const EmailPreviewDialog = (props: Props) => {
	const {
		open,
		title,
		data,
		loading = false,
		onSend = () => {},
		onCancel = () => {},
		sendLabel = 'Send',
		cancelLabel = 'Cancel',
		toLabel = 'To',
		ccLabel = 'Cc',
		subjectLabel = 'Subject',
		bodyLabel = 'Body',
		maxWidth = 'lg',
	} = props;

	const [fields, setFields] = useState({ to: '', cc: '', Subject: '', Body: '' });
	const bodyRef = useRef(null);

	useEffect(() => {
		if (!open) return;
		setFields({
			to: data?.to ?? '',
			cc: data?.cc ?? '',
			Subject: data?.Subject ?? '',
			Body: data?.Body ?? '',
		});
	}, [open, data?.id, data?.MsgId]);

	const isEditable = [true, 1, '1'].includes(data?.isPreview);

	const handleFieldChange = (field) => (e) => {
		setFields((prev) => ({ ...prev, [field]: e.target.value }));
	};

	const handleBodyBlur = (e) => {
		const html = e?.currentTarget?.innerHTML;
		if (html == null) return;
		setFields((prev) => ({ ...prev, Body: html }));
	};

	const handleSend = () => {
		const currentBody = (isEditable && bodyRef.current)
			? (bodyRef.current.innerHTML ?? fields.Body)
			: fields.Body;
		onSend({ ...fields, Body: currentBody });
	};


const handleRecipientKeyDown = (field) => (e) => {
    if (e.key !== "Enter" && e.key !== " ") return;

    setFields((prev) => {
        const value = prev[field] ?? "";

        if (e.key === "Enter") {
            e.preventDefault();

            const trimmedValue = value.trim();

            if (!trimmedValue || trimmedValue.endsWith(";")) {
                return prev;
            }

            return {
                ...prev,
                [field]: `${trimmedValue};`,
            };
        }

        if (value.trimEnd().endsWith(";")) {
            return prev;
        }
        e.preventDefault();
        const trimmedValue = value.trim();
        if (!trimmedValue) {
            return prev;
        }
        return {
            ...prev,
            [field]: `${trimmedValue};`,
        };
    });
};

	return (
		<Confirmation
			open={open}
			handleDialog={onCancel}
			yesBtnText={sendLabel}
			noBtnText={cancelLabel}
			loading={loading}
			title={title}
			maxWidth={maxWidth}
			otherDialogProps={{ fullWidth: true }}
			sx={{
				'& .MuiDialog-paper': {
					width: '95vw',
					maxWidth: '900px',
				}
			}}
			onYes={handleSend}
			onNo={onCancel}
		>
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
				<Box
					sx={{
						border: '1px solid',
						borderColor: 'divider',
						borderRadius: 1,
						backgroundColor: '#fff',
						overflow: 'hidden',
					}}
				>
					{[
						{ field: 'to', label: toLabel },
						{ field: 'cc', label: ccLabel },
						{ field: 'Subject', label: subjectLabel },
					].map(({ field, label }, idx) => (
						<Box
							key={field}
							sx={{
								display: 'flex',
								alignItems: 'center',
								borderBottom: idx < 2 ? '1px solid' : 'none',
								borderColor: 'divider',
								px: 1.5,
							}}
						>
							<FormLabel
								sx={{
									width: 110,
									flexShrink: 0,
									fontSize: 13,
									fontWeight: 600,
									color: 'text.secondary',
								}}
							>
								{label}
							</FormLabel>
							<TextField
								value={fields[field]}
								onChange={handleFieldChange(field)}
								onKeyDown={
									 (field === "to" || field === "cc")
										? handleRecipientKeyDown(field)
										: undefined
								}
								fullWidth
								variant="standard"
								size="small"
								slotProps={{ input: { disableUnderline: true } }}
								sx={{
									'& .MuiInputBase-input': { py: 1 },
									'& .MuiInputBase-input.Mui-disabled': {
										WebkitTextFillColor: 'currentColor',
										color: 'text.primary',
									},
								}}
							/>
						</Box>
					))}
				</Box>
				<Box>
					<FormLabel sx={{ mb: 1, display: 'block', fontWeight: 600 }}>{bodyLabel}</FormLabel>
						<DangerouslySetHtml
							htmlContent={fields.Body}
							isProcessed={true}
							sx={{
								border: '1px solid',
								borderColor: 'divider',
								borderRadius: 1,
								p: 2,
								minHeight: 280,
								maxHeight: '50vh',
								overflowY: 'auto',
								backgroundColor: '#fafafa',
								fontSize: 14,
								lineHeight: 1.6,
							}}
						/>
				</Box>
			</Box>
		</Confirmation>
	);
};

export default EmailPreviewDialog;
